import {
  clearSession,
  loadStoredSession,
  readHashToken,
  resolveApiBase,
  storeSession,
  stripHashToken,
  type StoredSession,
} from './session'
import { mountInspect } from './inspect'
import { openCaptureSheet } from './capture-sheet'
import {
  mountChrome,
  type MinePage,
  type MineStatusFilter,
  type ReviewerStatus,
  type SentFeedback,
} from './chrome'
import { mountPinLayer } from './pins'
import { getWithdrawalToken, withdrawFeedback } from './withdrawal-client'

async function validateSession(apiBase: string, token: string): Promise<StoredSession | null> {
  const origin = location.origin
  const url = `${apiBase}/api/site-feedback/session`
  const res = await fetch(url, { method: 'POST', headers: { Origin: origin, Authorization: `Bearer ${token}` } })
  if (!res.ok) return null
  const json = (await res.json()) as {
    data?: { expiresAt: string; actorLabel?: string | null }
  }
  if (!json.data?.expiresAt) return null
  return {
    token,
    expiresAt: json.data.expiresAt,
    actorLabel: json.data.actorLabel,
  }
}

function mapMineItem(it: {
  todoId?: string
  withdrawnAt?: string | null
  todoKey: string
  preview: string
  selector: string | null
  pageUrl: string | null
  createdAt: string
  reviewerStatus?: ReviewerStatus
  outcomeReason?: string | null
}): SentFeedback {
  return {
    todoId: it.todoId,
    canWithdraw: !!getWithdrawalToken(it.todoId),
    ownFeedback: !!getWithdrawalToken(it.todoId),
    withdrawnAt: it.withdrawnAt,
    todoKey: it.todoKey,
    preview: it.preview || it.todoKey,
    selector: it.selector || '',
    pageUrl: it.pageUrl || location.href.split('#')[0]!,
    at: Date.parse(it.createdAt) || Date.now(),
    reviewerStatus: it.reviewerStatus ?? 'open',
    outcomeReason: it.outcomeReason ?? null,
  }
}

async function loadMinePage(
  apiBase: string,
  token: string,
  opts: { status: MineStatusFilter; cursor?: string | null },
): Promise<MinePage> {
  const origin = location.origin
  const u = new URL(`${apiBase}/api/site-feedback/mine`)
  u.searchParams.set('status', opts.status)
  u.searchParams.set('limit', '50')
  if (opts.cursor) u.searchParams.set('cursor', opts.cursor)
  const res = await fetch(u.toString(), {
    headers: {
      Authorization: `Bearer ${token}`,
      Origin: origin,
    },
  })
  if (!res.ok) {
    return { items: [], nextCursor: null, counts: { open: 0, discarded: 0, done: 0 } }
  }
  const json = (await res.json()) as {
    data?: {
      items?: Array<{
        todoId?: string
        withdrawnAt?: string | null
        todoKey: string
        preview: string
        selector: string | null
        pageUrl: string | null
        createdAt: string
        reviewerStatus?: ReviewerStatus
        outcomeReason?: string | null
      }>
      nextCursor?: string | null
      counts?: { open: number; discarded: number; done: number }
    }
  }
  const items = (json.data?.items ?? []).map(mapMineItem)
  return {
    items,
    nextCursor: json.data?.nextCursor ?? null,
    counts: json.data?.counts ?? { open: 0, discarded: 0, done: 0 },
  }
}

export async function bootstrap(): Promise<void> {
  if ((window as unknown as { __klfbBooted?: boolean }).__klfbBooted) return
  ;(window as unknown as { __klfbBooted?: boolean }).__klfbBooted = true

  const script =
    (document.currentScript as HTMLScriptElement | null) ||
    document.querySelector('script[src*="site-feedback"]')

  const apiBase = resolveApiBase(script)
  const hashToken = readHashToken()
  let session = loadStoredSession()

  if (hashToken) {
    const validated = await validateSession(apiBase, hashToken)
    if (validated) {
      storeSession(validated)
      stripHashToken()
      session = validated
    } else {
      clearSession()
      stripHashToken()
      session = null
    }
  } else if (session) {
    const validated = await validateSession(apiBase, session.token)
    if (!validated) {
      clearSession()
      session = null
    } else {
      storeSession(validated)
      session = validated
    }
  }

  if (!session) return

  let armed = true
  let marking = false
  let sheetOpen = false

  const pins = mountPinLayer()

  const refreshPins = (items: SentFeedback[]) => {
    pins.render(
      items.map((it, i) => ({
        index: i + 1,
        selector: it.selector,
        pageUrl: it.pageUrl,
        status: it.reviewerStatus ?? 'open',
      })),
    )
  }

  const token = session.token

  const chrome = mountChrome({
    onWithdraw: (item) => {
      if (!item.todoId) return Promise.reject(new Error('Dieses Feedback kann hier nicht zurückgenommen werden.'))
      return withdrawFeedback(apiBase, token, item.todoId)
    },
    actorLabel: session.actorLabel,
    onLoadMine: (opts) => loadMinePage(apiBase, token, opts),
    onToggleMark: () => {
      if (!armed || sheetOpen) return
      marking = !marking
      chrome.setMarking(marking)
      if (!marking) inspect.clearSelection()
    },
    onMarkingChange: (on) => {
      marking = on
      if (!on) inspect.clearSelection()
    },
    onOpenList: () => {
      marking = false
      chrome.setMarking(false)
    },
    onRequestEnd: () => {
      marking = false
      chrome.setMarking(false)
    },
    onConfirmEnd: () => {
      armed = false
      marking = false
      const t = session?.token
      if (t) {
        void fetch(`${apiBase}/api/site-feedback/complete`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${t}`,
            'Content-Type': 'application/json',
            Origin: location.origin,
          },
          body: JSON.stringify({}),
          keepalive: true,
        }).catch(() => undefined)
      }
      clearSession()
      inspect.stop()
      pins.destroy()
      chrome.setMarking(false)
      chrome.showEnded()
    },
    onSentChange: refreshPins,
    onFocusSent: (item) => {
      const ok = pins.focus(item.selector)
      if (!ok) {
        /* selector stale after site edits */
      }
    },
  })

  const inspect = mountInspect(chrome.root, {
    isArmed: () => armed,
    isMarking: () => marking && !sheetOpen,
    onSelect: () => {
      /* selection painted by inspect */
    },
    onOpenSheet: (sel) => {
      if (!session || sheetOpen) return
      sheetOpen = true
      marking = false
      chrome.setMarking(false)
      openCaptureSheet({
        apiBase,
        token: session.token,
        selection: sel,
        onDone: () => {
          sheetOpen = false
          inspect.clearSelection()
        },
        onSent: (result, preview) => {
          chrome.addSent({
            todoId: result.todoId,
            canWithdraw: !!getWithdrawalToken(result.todoId),
            ownFeedback: !!getWithdrawalToken(result.todoId),
            todoKey: result.todoKey,
            preview,
            selector: sel.selector,
            pageUrl: location.href.split('#')[0]!,
            at: Date.now(),
            reviewerStatus: 'open',
          })
        },
      })
    },
  })

  // Initial pins: open feedbacks
  void loadMinePage(apiBase, token, { status: 'open' }).then((page) => {
    if (page.items.length) chrome.seedSent(page.items)
  })

  // KA-639: periodic heartbeat while magic session is active (Sitecheck)
  const HEARTBEAT_MS = 5 * 60 * 1000
  const sendHeartbeat = () => {
    if (!session) return
    void fetch(`${apiBase}/api/site-feedback/heartbeat`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.token}`,
        'Content-Type': 'application/json',
        Origin: location.origin,
      },
      body: JSON.stringify({}),
      keepalive: true,
    }).catch(() => undefined)
  }
  sendHeartbeat()
  window.setInterval(sendHeartbeat, HEARTBEAT_MS)

  // Pins nach Navigation / Resize neu setzen
  window.addEventListener('popstate', () => refreshPins(chrome.getSent().filter((s) => (s.reviewerStatus ?? 'open') === 'open')))
  window.addEventListener('resize', () => refreshPins(chrome.getSent().filter((s) => (s.reviewerStatus ?? 'open') === 'open')))
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      void bootstrap()
    })
  } else {
    void bootstrap()
  }
}
